import React, { useState } from 'react';
import {
    Badge,
    Box,
    Button,
    Card,
    CardBody,
    HStack,
    SimpleGrid,
    Stat,
    StatHelpText,
    StatLabel,
    StatNumber,
    Text,
    VStack,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import KpiProvenanceTag from './KpiProvenanceTag';

const roleKpiPresets = {
    compliance: {
        eyebrow: 'Business compliance KPIs',
        title: 'Audit-ready operating controls',
        description: 'KPI layer for supervisory review, evidence completeness, and export cadence.',
        items: [
            { label: 'SLA aging', value: '18h', help: 'Median open review queue age', provenance: 'inferred' },
            { label: 'Missing evidence', value: '7', help: 'Cases missing reviewer-ready support', provenance: 'inferred' },
            { label: 'Audit exports', value: '12', help: 'Exports generated in the current period', provenance: 'inferred' },
        ],
        actions: [
            { label: 'Review SLA queue', help: 'Open the oldest evidence requests before the next compliance review.', cta: 'Open queue', path: '/dashboard/compliance?view=sla' },
            { label: 'Generate audit period pack', help: 'Prepare a dated export for supervisory evidence and internal sign-off.', cta: 'Prepare export', path: '/reportsExports' },
            { label: 'Assign missing evidence', help: 'Route incomplete suitability support to the responsible RM.', cta: 'Assign cases', path: '/dashboard/compliance?view=evidence' },
        ],
    },
    head: {
        eyebrow: 'Business leadership KPIs',
        title: 'Advisory adoption and risk trend',
        description: 'KPI layer for management cadence, RM adoption, and action conversion.',
        items: [
            { label: 'Team adoption', value: '78%', help: 'Active RM seats in the last 30 days', provenance: 'inferred' },
            { label: 'Action conversion', value: '42%', help: 'Recommended actions marked complete', provenance: 'inferred' },
            { label: 'Risk trend', value: '-6.4%', help: 'Portfolio risk-pressure delta vs prior period', provenance: 'inferred' },
        ],
        actions: [
            { label: 'Coach low-adoption teams', help: 'Identify RM groups with weak usage and schedule enablement.', cta: 'Create coaching plan', path: '/dashboard/head?view=adoption' },
            { label: 'Prioritize conversion blockers', help: 'Review recommendations that stay open after the target SLA.', cta: 'Review blockers', path: '/dashboard/head?view=conversion' },
            { label: 'Export leadership pack', help: 'Create an executive summary for risk trend and adoption review.', cta: 'Prepare pack', path: '/reportsExports' },
        ],
    },
    portfolio_adviser: {
        eyebrow: 'Business portfolio KPIs',
        title: 'Suitability, drift, and portfolio fit',
        description: 'KPI layer for portfolio review prioritization and customer-level advice quality.',
        items: [
            { label: 'Mismatch severity', value: 'High 14', help: 'Clients with material profile-portfolio mismatch', provenance: 'inferred' },
            { label: 'Drift exposure', value: '23%', help: 'Book above target allocation drift threshold', provenance: 'inferred' },
            { label: 'Evidence complete', value: '86%', help: 'Suitability evidence completeness rate', provenance: 'inferred' },
        ],
        actions: [
            { label: 'Open severe mismatches', help: 'Review clients where risk profile and portfolio exposure diverge.', cta: 'Open reviews', path: '/dashboard/portfolio?view=mismatch' },
            { label: 'Queue rebalance checks', help: 'Send allocation drift cases into portfolio review workflow.', cta: 'Queue checks', path: '/dashboard/portfolio?view=drift' },
            { label: 'Complete suitability evidence', help: 'Close gaps before recommendations are exported.', cta: 'Complete evidence', path: '/dashboard/portfolio?view=evidence' },
        ],
    },
    view_only: {
        eyebrow: 'Business read-only KPIs',
        title: 'Dataset freshness and benchmark coverage',
        description: 'KPI layer for stakeholder review without editing rights.',
        items: [
            { label: 'Data freshness', value: 'Today', help: 'Latest dataset processing status', provenance: 'inferred' },
            { label: 'Coverage', value: '94%', help: 'Customers included in benchmarkable cohort', provenance: 'inferred' },
            { label: 'Benchmark delta', value: '-3.1%', help: 'Bias pressure delta vs previous period', provenance: 'inferred' },
        ],
    },
};

function RoleOperationalKpis({
    role,
    bg = 'white',
    borderColor = 'gray.200',
    titleColor = 'gray.800',
    descriptionColor = 'gray.600',
    accentColor = 'brand.navy',
    cardBg = 'gray.50',
    provenanceByLabel,
}) {
    const preset = roleKpiPresets[role];
    const navigate = useNavigate();
    const [selectedKpi, setSelectedKpi] = useState(preset?.items?.[0]?.label || '');
    const [queuedActions, setQueuedActions] = useState({});

    if (!preset) {
        return null;
    }

    const resolveProvenance = (item) => {
        if (provenanceByLabel && provenanceByLabel[item.label]) {
            return provenanceByLabel[item.label];
        }
        return item.provenance || 'inferred';
    };

    return (
        <Box
            bg={bg}
            borderWidth="1px"
            borderColor={borderColor}
            borderRadius="xl"
            boxShadow="md"
            p={{ base: 4, md: 5 }}
        >
            <VStack align="stretch" spacing={4}>
                <HStack justify="space-between" align="start" flexWrap="wrap" spacing={3}>
                    <Box>
                        <Badge colorScheme="purple" mb={2}>
                            {preset.eyebrow}
                        </Badge>
                        <Text fontSize={{ base: 'lg', md: 'xl' }} fontWeight="bold" color={titleColor}>
                            {preset.title}
                        </Text>
                        <Text mt={1} color={descriptionColor} fontSize="sm">
                            {preset.description}
                        </Text>
                    </Box>
                    <Badge colorScheme="yellow" borderRadius="full" px={3} py={1}>
                        Synthetic baseline
                    </Badge>
                </HStack>

                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
                    {preset.items.map((item) => {
                        const provenance = resolveProvenance(item);
                        return (
                            <Card
                                key={item.label}
                                bg={cardBg}
                                borderWidth="1px"
                                borderColor={selectedKpi === item.label ? accentColor : borderColor}
                                boxShadow={selectedKpi === item.label ? 'md' : 'sm'}
                                cursor="pointer"
                                onClick={() => setSelectedKpi(item.label)}
                                transition="all 0.2s"
                                _hover={{ transform: 'translateY(-2px)', boxShadow: 'md' }}
                            >
                                <CardBody>
                                    <Stat>
                                        <HStack justify="space-between" align="start" mb={1}>
                                            <StatLabel color={descriptionColor}>{item.label}</StatLabel>
                                            <KpiProvenanceTag status={provenance} />
                                        </HStack>
                                        <StatNumber color={accentColor} fontSize={{ base: '2xl', md: '3xl' }}>
                                            {item.value}
                                        </StatNumber>
                                        <StatHelpText color={descriptionColor} mb={0}>
                                            {item.help}
                                        </StatHelpText>
                                    </Stat>
                                </CardBody>
                            </Card>
                        );
                    })}
                </SimpleGrid>

                {preset.actions?.length ? (
                    <VStack align="stretch" spacing={3}>
                        <HStack justify="space-between" align="center" flexWrap="wrap">
                            <Box>
                                <Text fontSize="sm" fontWeight="bold" color={titleColor}>
                                    Recommended next actions
                                </Text>
                                <Text fontSize="xs" color={descriptionColor}>
                                    Select a KPI above, then queue an operational action for the role workflow.
                                </Text>
                            </Box>
                            <Badge colorScheme="blue" borderRadius="full" px={3} py={1}>
                                {selectedKpi}
                            </Badge>
                        </HStack>
                        <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={3}>
                            {preset.actions.map((action) => {
                                const isQueued = Boolean(queuedActions[action.label]);
                                return (
                                    <Card key={action.label} bg={cardBg} borderWidth="1px" borderColor={borderColor} boxShadow="sm">
                                        <CardBody>
                                            <VStack align="stretch" spacing={3}>
                                                <Box>
                                                    <Text fontWeight="semibold" color={titleColor}>{action.label}</Text>
                                                    <Text mt={1} fontSize="sm" color={descriptionColor}>{action.help}</Text>
                                                </Box>
                                                <Button
                                                    size="sm"
                                                    colorScheme={isQueued ? 'green' : 'blue'}
                                                    variant={isQueued ? 'solid' : 'outline'}
                                                    onClick={() => {
                                                        setQueuedActions((prev) => ({
                                                            ...prev,
                                                            [action.label]: true,
                                                        }));
                                                        if (action.path) navigate(action.path);
                                                    }}
                                                >
                                                    {isQueued ? 'Queued' : action.cta}
                                                </Button>
                                            </VStack>
                                        </CardBody>
                                    </Card>
                                );
                            })}
                        </SimpleGrid>
                    </VStack>
                ) : null}
            </VStack>
        </Box>
    );
}

export default RoleOperationalKpis;
